// Horse Profile Form Components
// These are extracted from HorseProfile to improve maintainability

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, X } from 'lucide-react';

// Country list for international travel
export const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Belgium", "Brazil", "Canada", "Chile",
  "China", "Colombia", "Czech Republic", "Denmark", "Egypt", "Finland", "France", "Germany", "Greece", "Hong Kong",
  "Hungary", "India", "Indonesia", "Ireland", "Israel", "Italy", "Japan", "Kenya", "Malaysia", "Mexico",
  "Morocco", "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar", "Romania", "Russia", "Saudi Arabia", "Singapore", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland",
  "Taiwan", "Thailand", "Turkey", "UAE", "Ukraine", "United Kingdom", "United States", "Venezuela", "Vietnam"
];

// Frequency display helper
export const frequencyLabels = {
  '1x_daily': '1x Daily',
  '2x_daily': '2x Daily',
  'pre_ride': 'Pre-Ride',
  'post_ride': 'Post-Ride',
  'weekly': 'Weekly',
  'monthly': 'Monthly',
  'as_required': 'As Required'
};

// Feed Form Component
export const FeedForm = ({ data, onChange, onSubmit, isEdit = false, saveAsNew, setSaveAsNew }) => {
  const addFeedItem = () => {
    onChange({
      ...data,
      items: [...(data.items || []), { item_name: '', amount: '', frequency: '1x_daily' }]
    });
  };

  const updateFeedItem = (index, field, value) => {
    const newItems = [...(data.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange({ ...data, items: newItems });
  };

  const removeFeedItem = (index) => {
    const newItems = (data.items || []).filter((_, i) => i !== index);
    onChange({ ...data, items: newItems });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      {isEdit && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="save-as-new"
              checked={saveAsNew || false}
              onChange={(e) => setSaveAsNew(e.target.checked)}
              className="rounded border-amber-300"
              data-testid="feed-save-as-new-checkbox"
            />
            <label htmlFor="save-as-new" className="text-sm text-amber-800 cursor-pointer">
              Save as new feeding schedule (creates new entry with today's date)
            </label>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Date *</Label>
        <Input 
          type="date" 
          value={data.date || ''} 
          onChange={(e) => onChange({ ...data, date: e.target.value })} 
          required 
          data-testid="feed-date-input"
          disabled={isEdit && saveAsNew}
        />
        {isEdit && saveAsNew && (
          <p className="text-xs text-muted-foreground">New entry will use today's date</p>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Feed Items</Label>
          <Button type="button" variant="outline" size="sm" onClick={addFeedItem} className="gap-1" data-testid="add-feed-item-btn">
            <Plus className="w-3 h-3" /> Add Item
          </Button>
        </div>

        {(data.items || []).length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No feed items added. Click "Add Item" to start.</p>
        ) : (
          <div className="space-y-3">
            {(data.items || []).map((item, index) => (
              <div key={index} className="p-3 border rounded-lg bg-muted/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Item {index + 1}</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => removeFeedItem(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <Input
                    placeholder="Feed item name (e.g., Oats, Hay)"
                    value={item.item_name || ''}
                    onChange={(e) => updateFeedItem(index, 'item_name', e.target.value)}
                    required
                    data-testid={`feed-item-name-${index}`}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Amount (e.g., 2 scoops)"
                      value={item.amount || ''}
                      onChange={(e) => updateFeedItem(index, 'amount', e.target.value)}
                      required
                      data-testid={`feed-item-amount-${index}`}
                    />
                    <Select 
                      value={item.frequency || '1x_daily'} 
                      onValueChange={(v) => updateFeedItem(index, 'frequency', v)}
                    >
                      <SelectTrigger data-testid={`feed-item-frequency-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1x_daily">1x Daily</SelectItem>
                        <SelectItem value="2x_daily">2x Daily</SelectItem>
                        <SelectItem value="pre_ride">Pre-Ride</SelectItem>
                        <SelectItem value="post_ride">Post-Ride</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="as_required">As Required</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea 
          value={data.notes || ''} 
          onChange={(e) => onChange({ ...data, notes: e.target.value })} 
          placeholder="Any additional notes about this feed schedule..."
          data-testid="feed-notes-input"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full bg-forest hover:bg-forest-dark" 
        disabled={(data.items || []).length === 0}
        data-testid="feed-submit-btn"
      >
        {isEdit ? 'Update Feed Entry' : 'Add Feed Entry'}
      </Button>
    </form>
  );
};

// History Event Form Component
export const HistoryEventForm = ({ data, onChange, onSubmit, isEdit = false }) => {
  const showCountry = data.event_type === 'international_travel';
  const showTrainer = data.event_type === 'broken_in' || data.event_type === 'training';
  const showDescription = data.event_type === 'other';
  const showOwnerName = data.event_type === 'new_owner';

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label>Event Type *</Label>
        <Select value={data.event_type || ''} onValueChange={(v) => onChange({ ...data, event_type: v })}>
          <SelectTrigger data-testid="history-event-type-select">
            <SelectValue placeholder="Select event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new_owner">New Owner</SelectItem>
            <SelectItem value="international_travel">International Travel</SelectItem>
            <SelectItem value="broken_in">Broken In</SelectItem>
            <SelectItem value="training">Training</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          For Gelding or Foaling events, use the Health tab instead.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Date *</Label>
        <Input 
          type="date" 
          value={data.date || ''} 
          onChange={(e) => onChange({ ...data, date: e.target.value })} 
          required 
          data-testid="history-event-date-input"
        />
      </div>

      {showOwnerName && (
        <div className="space-y-2">
          <Label>Owner Name *</Label>
          <Input 
            value={data.owner_name || ''} 
            onChange={(e) => onChange({ ...data, owner_name: e.target.value })} 
            placeholder="Enter owner's name"
            required
            data-testid="history-owner-name-input"
          />
        </div>
      )}

      {showCountry && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>From Country *</Label>
            <Select value={data.from_country || ''} onValueChange={(v) => onChange({ ...data, from_country: v })}>
              <SelectTrigger data-testid="history-from-country-select">
                <SelectValue placeholder="Select origin country" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {COUNTRIES.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>To Country *</Label>
            <Select value={data.to_country || ''} onValueChange={(v) => onChange({ ...data, to_country: v })}>
              <SelectTrigger data-testid="history-to-country-select">
                <SelectValue placeholder="Select destination country" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {COUNTRIES.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {showTrainer && (
        <div className="space-y-2">
          <Label>Trainer</Label>
          <Input 
            value={data.trainer || ''} 
            onChange={(e) => onChange({ ...data, trainer: e.target.value })} 
            placeholder="Trainer's name"
            data-testid="history-trainer-input"
          />
        </div>
      )}

      {showDescription && (
        <div className="space-y-2">
          <Label>Description *</Label>
          <Input 
            value={data.description || ''} 
            onChange={(e) => onChange({ ...data, description: e.target.value })} 
            placeholder="Event description"
            required
            data-testid="history-description-input"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea 
          value={data.notes || ''} 
          onChange={(e) => onChange({ ...data, notes: e.target.value })} 
          placeholder="Additional notes..."
          data-testid="history-notes-input"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full bg-forest hover:bg-forest-dark"
        disabled={!data.event_type || !data.date}
        data-testid="history-submit-btn"
      >
        {isEdit ? 'Update Event' : 'Add Event'}
      </Button>
    </form>
  );
};

// Health Form Component
export const HealthForm = ({ data, onChange, onSubmit, isEdit = false, onPhotoUpload, photoUploading }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Record Type</Label>
      <Select value={data.record_type} onValueChange={(v) => onChange({ ...data, record_type: v, vet_visit_type: v === 'veterinary' ? 'checkup' : null, sire: null, service_type: null, scan_result: null, foal_name: null, outcome: null, number_of_foals: null, delivery_type: null })}>
        <SelectTrigger data-testid="health-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="vaccination">Vaccination</SelectItem>
          <SelectItem value="worming">Worming</SelectItem>
          <SelectItem value="farrier">Farrier</SelectItem>
          <SelectItem value="dentist">Dentist</SelectItem>
          <SelectItem value="veterinary">Veterinary</SelectItem>
          <SelectItem value="gelding">Gelding</SelectItem>
          <SelectItem value="bodywork">Body Work</SelectItem>
          <SelectItem value="medication">Medication</SelectItem>
          <SelectItem value="insemination">Insemination</SelectItem>
          <SelectItem value="pregnancy_scan">Breeding Scan</SelectItem>
          <SelectItem value="foaling">Foaling</SelectItem>
        </SelectContent>
      </Select>
    </div>
    {/* Gelding - info note about auto-history */}
    {data.record_type === 'gelding' && (
      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-sm text-purple-700">
        This will also be added to the horse's History timeline.
      </div>
    )}
    {/* Foaling fields */}
    {data.record_type === 'foaling' && (
      <>
        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-sm text-orange-700">
          This will also be added to the horse's History timeline.
        </div>
        <div className="space-y-2">
          <Label>Sire (Stallion)</Label>
          <Input value={data.sire || ''} onChange={(e) => onChange({ ...data, sire: e.target.value })} placeholder="Stallion name" data-testid="foaling-sire-input" />
        </div>
        <div className="space-y-2">
          <Label>Foal's Name</Label>
          <Input value={data.foal_name || ''} onChange={(e) => onChange({ ...data, foal_name: e.target.value })} placeholder="Foal name (if known)" data-testid="foaling-foal-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Outcome</Label>
            <Select value={data.outcome || ''} onValueChange={(v) => onChange({ ...data, outcome: v })}>
              <SelectTrigger data-testid="foaling-outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="live_foal">Live Foal</SelectItem>
                <SelectItem value="stillbirth">Stillbirth</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Foal Sex</Label>
            <Select value={data.foal_sex || ''} onValueChange={(v) => onChange({ ...data, foal_sex: v })}>
              <SelectTrigger data-testid="foaling-sex-select"><SelectValue placeholder="Select sex" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="colt">Colt</SelectItem>
                <SelectItem value="filly">Filly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Number of Foals</Label>
            <Select value={data.number_of_foals || ''} onValueChange={(v) => onChange({ ...data, number_of_foals: v })}>
              <SelectTrigger data-testid="foaling-number-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="twin">Twin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Delivery Type</Label>
            <Select value={data.delivery_type || ''} onValueChange={(v) => onChange({ ...data, delivery_type: v })}>
              <SelectTrigger data-testid="foaling-delivery-select"><SelectValue placeholder="Select delivery type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal Foaling</SelectItem>
                <SelectItem value="assisted">Assisted Delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </>
    )}
    {data.record_type === 'veterinary' && (
      <div className="space-y-2">
        <Label>Visit Type</Label>
        <Select value={data.vet_visit_type || 'checkup'} onValueChange={(v) => onChange({ ...data, vet_visit_type: v })}>
          <SelectTrigger data-testid="vet-visit-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="illness">Illness</SelectItem>
            <SelectItem value="injury">Injury</SelectItem>
            <SelectItem value="pre_purchase">Pre-Purchase Examination</SelectItem>
            <SelectItem value="checkup">Check Up</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
    {data.record_type === 'farrier' && (
      <div className="space-y-2">
        <Label>Service Type</Label>
        <Select value={data.farrier_type || 'shoeing'} onValueChange={(v) => onChange({ ...data, farrier_type: v })}>
          <SelectTrigger data-testid="farrier-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="shoeing">Shoeing</SelectItem>
            <SelectItem value="trim">Trim</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
    {data.record_type === 'insemination' && (
      <>
        <div className="space-y-2">
          <Label>Sire</Label>
          <Input value={data.sire || ''} onChange={(e) => onChange({ ...data, sire: e.target.value })} placeholder="Stallion name" data-testid="insemination-sire-input" />
        </div>
        <div className="space-y-2">
          <Label>Service Type</Label>
          <Select value={data.service_type || ''} onValueChange={(v) => onChange({ ...data, service_type: v })}>
            <SelectTrigger data-testid="insemination-service-select"><SelectValue placeholder="Select service type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="natural">Natural</SelectItem>
              <SelectItem value="ai_fresh">AI - Fresh</SelectItem>
              <SelectItem value="ai_frozen">AI - Frozen</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </>
    )}
    {data.record_type === 'pregnancy_scan' && (
      <div className="space-y-2">
        <Label>Scan Result</Label>
        <Select value={data.scan_result || ''} onValueChange={(v) => onChange({ ...data, scan_result: v, foal_due_date: v !== 'positive' ? null : data.foal_due_date, create_foal_reminder: v !== 'positive' ? false : data.create_foal_reminder })}>
          <SelectTrigger data-testid="pregnancy-result-select"><SelectValue placeholder="Select result" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="positive">Positive Pregnancy</SelectItem>
            <SelectItem value="negative">Negative Pregnancy</SelectItem>
            <SelectItem value="routine">Routine Scan</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
    {data.record_type === 'pregnancy_scan' && data.scan_result === 'positive' && (
      <>
        <div className="space-y-2">
          <Label>Foal Due Date</Label>
          <Input type="date" value={data.foal_due_date || ''} onChange={(e) => onChange({ ...data, foal_due_date: e.target.value })} data-testid="foal-due-date-input" />
        </div>
        {data.foal_due_date && (
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="create-foal-reminder" 
              checked={data.create_foal_reminder || false} 
              onCheckedChange={(checked) => onChange({ ...data, create_foal_reminder: checked })}
              data-testid="create-foal-reminder-checkbox"
            />
            <label htmlFor="create-foal-reminder" className="text-sm cursor-pointer">
              Add foal due date as a reminder
            </label>
          </div>
        )}
      </>
    )}
    <div className="space-y-2">
      <Label>Provider</Label>
      <Input value={data.provider || ''} onChange={(e) => onChange({ ...data, provider: e.target.value })} placeholder="Vet/Farrier name" data-testid="health-provider-input" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Date *</Label>
        <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required data-testid="health-date-input" />
      </div>
      <div className="space-y-2">
        <Label>Next Due Date</Label>
        <Input type="date" value={data.next_due_date || ''} onChange={(e) => onChange({ ...data, next_due_date: e.target.value })} data-testid="health-next-due-input" />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Notes</Label>
      <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} data-testid="health-notes-input" />
    </div>
    {data.record_type === 'veterinary' && isEdit && data.record_id && (
      <div className="space-y-2">
        <Label>Photos ({(data.photos || []).length}/3)</Label>
        <div className="grid grid-cols-3 gap-2">
          {(data.photos || []).map((photoUrl, idx) => (
            <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`} alt={`Vet photo ${idx + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
          {(data.photos || []).length < 3 && (
            <div 
              className="aspect-square rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-forest transition-colors"
              onClick={onPhotoUpload}
            >
              {photoUploading ? (
                <div className="animate-spin w-6 h-6 border-2 border-forest border-t-transparent rounded-full" />
              ) : (
                <Plus className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Click + to add photos (max 3). Photos stay with this record.</p>
      </div>
    )}
    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-health-btn">{isEdit ? 'Update Record' : 'Add Record'}</Button>
  </form>
);

// Ride Type Options
export const RIDE_TYPE_OPTIONS = [
  { value: 'flatwork', label: 'Flatwork' },
  { value: 'jumping', label: 'Jumping' },
  { value: 'trail', label: 'Trail Ride' },
  { value: 'lesson', label: 'Lesson' },
  { value: 'hack', label: 'Hack' },
  { value: 'competition', label: 'Competition' },
  { value: 'dressage', label: 'Dressage' },
  { value: 'lunging', label: 'Lunging' },
  { value: 'groundwork', label: 'Groundwork' },
  { value: 'trackwork', label: 'Trackwork' }
];

// Ride Form Component
export const RideForm = ({ data, onChange, onSubmit, isEdit = false, riders = [] }) => {
  const [showCustomType, setShowCustomType] = useState(false);
  const [customTypeInput, setCustomTypeInput] = useState('');
  const [showOtherRider, setShowOtherRider] = useState(false);
  
  const selectedTypes = data.ride_types || (data.ride_type ? [data.ride_type] : []);
  
  const handleTypeToggle = (typeValue) => {
    const newTypes = selectedTypes.includes(typeValue)
      ? selectedTypes.filter(t => t !== typeValue)
      : [...selectedTypes, typeValue];
    onChange({ ...data, ride_types: newTypes });
  };
  
  const handleAddCustomType = () => {
    if (customTypeInput.trim()) {
      const newTypes = [...selectedTypes, customTypeInput.trim()];
      onChange({ ...data, ride_types: newTypes, custom_ride_type: customTypeInput.trim() });
      setCustomTypeInput('');
      setShowCustomType(false);
    }
  };

  const handleRiderChange = (value) => {
    if (value === 'other') {
      setShowOtherRider(true);
      onChange({ ...data, rider: '' });
    } else {
      setShowOtherRider(false);
      onChange({ ...data, rider: value });
    }
  };

  // Check if current rider value matches any rider profile
  const isRiderFromProfiles = riders.some(r => r.name === data.rider);
  
  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label>Rider *</Label>
        {riders.length > 0 && !showOtherRider && (isEdit ? isRiderFromProfiles || !data.rider : true) ? (
          <Select value={data.rider || ''} onValueChange={handleRiderChange}>
            <SelectTrigger data-testid="ride-rider-select">
              <SelectValue placeholder="Select rider" />
            </SelectTrigger>
            <SelectContent>
              {riders.map(rider => (
                <SelectItem key={rider.rider_id} value={rider.name}>
                  {rider.name}
                </SelectItem>
              ))}
              <SelectItem value="other">Other (type name)</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="flex gap-2">
            <Input 
              value={data.rider || ''} 
              onChange={(e) => onChange({ ...data, rider: e.target.value })} 
              placeholder="Enter rider name"
              required 
              data-testid="ride-rider-input" 
            />
            {riders.length > 0 && (
              <Button type="button" variant="outline" size="icon" onClick={() => { setShowOtherRider(false); onChange({ ...data, rider: '' }); }}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required data-testid="ride-date-input" />
        </div>
        <div className="space-y-2">
          <Label>Duration (min)</Label>
          <Input type="number" value={data.duration || ''} onChange={(e) => onChange({ ...data, duration: e.target.value })} data-testid="ride-duration-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Ride Type(s) - Select multiple</Label>
        <div className="flex flex-wrap gap-2">
          {RIDE_TYPE_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              type="button"
              variant={selectedTypes.includes(opt.value) ? "default" : "outline"}
              size="sm"
              onClick={() => handleTypeToggle(opt.value)}
              className={selectedTypes.includes(opt.value) ? 'bg-forest hover:bg-forest-dark' : ''}
              data-testid={`ride-type-${opt.value}`}
            >
              {opt.label}
            </Button>
          ))}
          {selectedTypes.filter(t => !RIDE_TYPE_OPTIONS.find(o => o.value === t)).map(customType => (
            <Button
              key={customType}
              type="button"
              variant="default"
              size="sm"
              onClick={() => handleTypeToggle(customType)}
              className="bg-saddle hover:bg-saddle/80"
            >
              {customType} X
            </Button>
          ))}
        </div>
        {!showCustomType ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowCustomType(true)} className="text-forest mt-1" data-testid="add-custom-type-btn">
            + Add Custom Type
          </Button>
        ) : (
          <div className="flex gap-2 mt-2">
            <Input 
              value={customTypeInput} 
              onChange={(e) => setCustomTypeInput(e.target.value)} 
              placeholder="Enter custom ride type"
              className="flex-1"
              data-testid="custom-type-input"
            />
            <Button type="button" onClick={handleAddCustomType} size="sm" className="bg-forest hover:bg-forest-dark" data-testid="save-custom-type-btn">Add</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCustomType(false); setCustomTypeInput(''); }}>Cancel</Button>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} data-testid="ride-notes-input" />
      </div>
      <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-ride-btn">{isEdit ? 'Update Ride' : 'Log Ride'}</Button>
    </form>
  );
};

// Event Form Component
export const EventForm = ({ data, onChange, onSubmit, isEdit = false }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Event Name *</Label>
      <Input value={data.event_name || ''} onChange={(e) => onChange({ ...data, event_name: e.target.value })} required />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Date *</Label>
        <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label>Location</Label>
        <Input value={data.location || ''} onChange={(e) => onChange({ ...data, location: e.target.value })} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Discipline</Label>
        <Input value={data.discipline || ''} onChange={(e) => onChange({ ...data, discipline: e.target.value })} placeholder="e.g., Dressage" />
      </div>
      <div className="space-y-2">
        <Label>Class</Label>
        <Input value={data.event_class || ''} onChange={(e) => onChange({ ...data, event_class: e.target.value })} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Placing</Label>
        <Input value={data.placing || ''} onChange={(e) => onChange({ ...data, placing: e.target.value })} placeholder="e.g., 1st" />
      </div>
      <div className="space-y-2">
        <Label>Score</Label>
        <Input value={data.score || ''} onChange={(e) => onChange({ ...data, score: e.target.value })} />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Notes</Label>
      <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} />
    </div>
    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark">{isEdit ? 'Update Event' : 'Add Event'}</Button>
  </form>
);

// Expense Form Component
export const ExpenseForm = ({ data, onChange, onSubmit, isEdit = false }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Category *</Label>
      <Select value={data.category || ''} onValueChange={(v) => onChange({ ...data, category: v })}>
        <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="vet">Vet</SelectItem>
          <SelectItem value="farrier">Farrier</SelectItem>
          <SelectItem value="feed">Feed</SelectItem>
          <SelectItem value="gear">Tack</SelectItem>
          <SelectItem value="entry_fee">Entry Fee</SelectItem>
          <SelectItem value="boarding">Boarding</SelectItem>
          <SelectItem value="training">Training</SelectItem>
          <SelectItem value="lesson">Lesson</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Amount *</Label>
        <Input type="number" step="0.01" value={data.amount || ''} onChange={(e) => onChange({ ...data, amount: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label>Date *</Label>
        <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Vendor</Label>
      <Input value={data.vendor || ''} onChange={(e) => onChange({ ...data, vendor: e.target.value })} />
    </div>
    <div className="space-y-2">
      <Label>Notes</Label>
      <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} />
    </div>
    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark">{isEdit ? 'Update Expense' : 'Add Expense'}</Button>
  </form>
);
